import React, { useState } from 'react';
import { BetslipContext } from '../../data/betslip/BetslipConf';
import { getConfiguration } from '../../data/utils/configuration';
import { getCurrencySymbol } from '../../data/utils/getCurrencySymbol';
import { getLocaleDotRound } from '../../data/utils/getLocaleDotRound';
import { getPrintableAmount } from '../../data/utils/getPrintableAmount';
import { getValidAmountString } from '../../data/utils/getValidAmountString';
import { getAmountInCent } from '../../data/utils/getAmountInCent';
import { getReservationCodeType } from '../../data/utils/getReservationCodeType';
import { setReservationCodeType } from '../../data/utils/setReservationCodeType';
import { labels } from '../../data/labels';
import Keypad from './Keypad';

function getTogglesToShow(conf) {
    const togglesToShow = new Set();
    Object.values(conf.vls).forEach(v => {
        if (v.sh === true) {
            togglesToShow.add(v.ty);
        }
    });
    return togglesToShow;
}

function Settings(props) {
    const conf = getConfiguration();
    const betslipContext = React.useContext(BetslipContext);
    const [showKeypad, setShowKeypad] = useState(false);
    const [showError, setShowError] = useState(false);
    const [togglesToShow] = useState(getTogglesToShow(conf.ovs));
    const [useQrCode, setUseQrCode] = useState(getReservationCodeType() === 'qrCode');

    let betslipData = betslipContext.getBetslipData();
    const couponKey = betslipData.selectedCoupon;
    const coupon = betslipData.coupons[couponKey];
    let varOdd = coupon.oddVariation;

    const defaultAmount = getPrintableAmount(betslipData.defaultAmount, conf.cs.m, conf.cs.nd);
    const defaultMinAmount = getPrintableAmount(betslipContext.minimumBetAmount, conf.cs.m, conf.cs.nd);
    const unitMultAmount = getPrintableAmount(betslipContext.minimumUnitAmount, conf.cs.m, conf.cs.nd);
    const currency = getCurrencySymbol(conf.cs.ct);
	let charDecimalBrowser = getLocaleDotRound().round;

    function changeVarOdd(oddVariation) {
		betslipContext.changeOddVariation(oddVariation, true);
    }
    
    function toggleSaveBetslip() {
    	betslipContext.toggleSaveBetslipAfterPurchase();    
    }

    function toggleKeypad() {
        setShowKeypad(!showKeypad);
    }

    function closeKeypad() {
        setShowKeypad(false);
    }

    function toggleReservationPrintType() {
        if (useQrCode) {
            setReservationCodeType('barcode');
        } else {
            setReservationCodeType('qrCode');
        }
        setUseQrCode(!useQrCode);
    }

    function setDefaultAmount(defaultAmount) {
        // get a valid input field value for this amount: only number and dot, and max nDec digit after it
        let defaultAmountInCents = getValidAmountString(defaultAmount);
        // if is number, I calculate the new amount and I keep it into the field value. 
        // If not, I keep the field but I use the last valid amount for the calcs
        if (!isNaN(defaultAmountInCents) && defaultAmountInCents.length > 0) {
            const mult = conf.cs.m;
            defaultAmountInCents = getAmountInCent(parseFloat(defaultAmountInCents), mult);
        }

        const amountUnit = betslipContext.minimumUnitAmount;
		const isMultiple = (defaultAmountInCents % amountUnit) === 0;
		if (defaultAmountInCents < betslipContext.minimumBetAmount || isMultiple === false ) {
			setShowError(true);
		} else {
            betslipContext.setDefaultAmount(defaultAmountInCents);
            setShowError(false);
        }
    }

    return (
        <>
            <div className="customInput customInput--toggle">
                <label>
                    <input type="checkbox" checked={useQrCode} onChange={toggleReservationPrintType} />
                    <span></span>
                    <div className="customInput__description">{labels['betslip.use_qr_code_for_reserved_ticked']}</div>
                </label>
            </div>
            {togglesToShow.size > 0 &&
                <span>{labels['betslip.accept_odds_variation']}</span>
            }
            {togglesToShow.has('NEVER') &&
                <div className="customInput customInput--radio">
                    <label>
                        <input type="radio" name="radio" checked={varOdd === 'NEVER'} onChange={() => changeVarOdd('NEVER')} />
                        <span></span>
                        <div className="customInput__description">{labels['betslip.accept_odds_variation.never']}</div>
                    </label>
                </div>
            }
            {togglesToShow.has('ALWAYS') &&
                <div className="customInput customInput--radio">
                    <label>
                        <input type="radio" name="radio" checked={varOdd === 'ALWAYS'} onChange={() => changeVarOdd('ALWAYS')} />
                        <span></span>
                        <div className="customInput__description">{labels['betslip.accept_odds_variation.always']}</div>
                    </label>
                </div>
            }
            {togglesToShow.has('INCR') &&
                <div className="customInput customInput--radio">
                    <label>
                        <input type="radio" name="radio" checked={varOdd === 'INCR'} onChange={() => changeVarOdd('INCR')} />
                        <span></span>
                        <div className="customInput__description">{labels['betslip.accept_odds_variation.increasing']}</div>
                    </label>
                </div>
            }
            <div className="customInput customInput--toggle">
                <label>
                    <input type="checkbox" checked={betslipContext.isSaveBetslipAfterPurchaseEnabled()} onChange={toggleSaveBetslip} />
                    <span></span>
                    <div className="customInput__description">{labels['betslip.save_ticket']}</div>
                </label>
            </div>
            <div className="cart__footer__betInputRow">
                <span>{labels['betslip.default_amount']}</span>
                <div className="calculatorInput" onClick={toggleKeypad}>{defaultAmount} {currency}</div>
                {showKeypad &&
                    <Keypad amount={defaultAmount}
                            currency={currency}
                            decimalsEnabled={true}
                            decimalSeparator={charDecimalBrowser}
                            setAmount={setDefaultAmount}
                            close={closeKeypad} />
                }
            </div>
            {showError &&
                <div className="text-small">{labels['betslip.error.minimum_amount_part1']} {defaultMinAmount} {currency} {labels['betslip.error.minimum_amount_part2']} {unitMultAmount} {currency}</div>
            }
        </>
    );
}

export default Settings;
